import React, { useEffect } from 'react';
import find from 'lodash/find';
import reduxWrapper from '@src/store';
import * as actions from '@src/store/actions';
import * as selectors from '@src/store/selectors';
import HomePage from '@src/pages/home/HomePage';
import { defaultActionCalls, waitForStoreSettled, isSideEffectSettled, SSR_HARD_DEADLINE_MS } from '@src/router/ssr';
import { SSR_PAGE_HOME } from '@src/router/hydrate';
import { getApiData, getPageTimeoutData } from '@src/utils/deprecatedCommon';
import { getSearchState } from '@src/store/selectors/category';
import { useSelector, useDispatch } from 'react-redux';

const homePagesNeedData = ({ list = [], type }) => {
	const target = find(list, { type });
	return target && !target.hide;
};

const getHomeThemeList = themes => {
	const pages = themes?.pages || [];
	const homeTheme = find(pages, ({ content }) => content.indexOf('"key":"home"') > -1);
	return JSON.parse(homeTheme?.content || '{}')?.list || [];
};

const Home = ssrProps => {
	const dispatch = useDispatch();
	const { themes } = useSelector(selectors.getThemeSelector);
	const homeThemeList = getHomeThemeList(themes);
	useEffect(() => {
		const ssrHeaders = {
			'x-school-hostname': process.env.NEXT_PUBLIC_HOSTNAME,
		};
		if (homePagesNeedData({ list: homeThemeList, type: 'courseProducts' })) {
			dispatch(actions.getCourseViewCall({ ssrHeaders }));
		}
		if (homePagesNeedData({ list: homeThemeList, type: 'offlineEvents' })) {
			dispatch(actions.getEventsCall({ ssrHeaders }));
		}
		if (themes?.enable_learning_map_and_area) {
			if (homePagesNeedData({ list: homeThemeList, type: 'learningPath' })) {
				dispatch(actions.getLearningPathsCall({ ssrHeaders }));
			}
			if (homePagesNeedData({ list: homeThemeList, type: 'learningArea' })) {
				dispatch(actions.getLearningAreasCall({ ssrHeaders }));
			}
		}
		// 相容舊學校
		dispatch(actions.getWebinarsCall({ ssrHeaders }));
	}, []);

	return <HomePage ssrProps={ssrProps} />;
};

export const getServerSideProps = reduxWrapper.getServerSideProps(
	async ctx => {
		const { store, req } = ctx;
		const { ssrHeaders } = getApiData(req);
		const time = 3000;
		const pageTimeout = setTimeout(() => {
			const pageTimeoutData = getPageTimeoutData({ req, time });

			if (pageTimeoutData) {
				store.dispatch(
					actions.sendBugMsgCall({ msg: JSON.stringify(pageTimeoutData) }),
				);
			}
		}, time);

		store.dispatch(actions.setSSRPage(SSR_PAGE_HOME));

		const themes = store.getState()?.theme?.themes;
		const homeThemeList = getHomeThemeList(themes);
		const getCourses = homePagesNeedData({ list: homeThemeList, type: 'courseProducts' });
		const getEvents = homePagesNeedData({ list: homeThemeList, type: 'offlineEvents' });

		await waitForStoreSettled({
			store,
			isSettled: () => {
				const state = store.getState();
				const defaultActionDone = defaultActionCalls.actionDone(ctx);
				const eventsState = selectors.getEventsState(state);
				const { coursesViewState } = selectors.selectCourse(state);
				const { webinarsListState } = selectors.selectWebinar(state);
				const searchState = getSearchState(state);
				return (
					defaultActionDone &&
					(getEvents ? isSideEffectSettled(eventsState) : true) &&
					(getCourses ? isSideEffectSettled(coursesViewState) : true) &&
					isSideEffectSettled(webinarsListState) &&
					(getCourses ? isSideEffectSettled(searchState) : true)
				);
			},
			startCalls: () => {
				defaultActionCalls.actionCall(ctx);

				if (getEvents) {
					store.dispatch(actions.getEventsCall({ ssrHeaders }));
				}
				if (getCourses) {
					store.dispatch(actions.getCourseViewCall({ ssrHeaders }));
					store.dispatch(actions.getCommoditySearchCall({ ssrHeaders, page_size: 8 }));
				}
				// 相容舊學校
				store.dispatch(actions.getWebinarsCall({ ssrHeaders }));

				if (themes?.enable_learning_map_and_area) {
					if (homePagesNeedData({ list: homeThemeList, type: 'learningPath' })) {
						store.dispatch(actions.getLearningPathsCall({ ssrHeaders }));
					}
					if (homePagesNeedData({ list: homeThemeList, type: 'learningArea' })) {
						store.dispatch(actions.getLearningAreasCall({ ssrHeaders }));
					}
				}
			},
			onDeadline: () => {
				const deadlineData = getPageTimeoutData({ req, time: SSR_HARD_DEADLINE_MS });
				if (deadlineData) {
					store.dispatch(actions.sendBugMsgCall({
						msg: JSON.stringify({ ...deadlineData, msg: 'ssrHardDeadline' }),
					}));
				}
			},
		});

		clearTimeout(pageTimeout);

		const { themeState } = selectors.getThemeSelector(store.getState());
		if (themeState === 'PAYMENT_REQUIRED' || themeState === 'FAIL') {
			// eslint-disable-next-line no-param-reassign
			ctx.res.statusCode = 404;
		}
	},
);

export default Home;
